import React from 'react';
import AnchorLink from 'components/Models/AnchorLink/AnchorLink';
import Link from 'components/Models/Link/Link';
import InvestorLinkButton from 'components/InvestorLinkButton';
import { scrollToBlock } from 'js/scroll-utils';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './InvestorSecondaryMenu.scss';

const INVESTOR_PAGE_SECONDARY_MENU_STICKY_CLASSNAME = 'investor-page-sticky-secondary-menu';
const ANCHOR_SCROLL_ANIMATION_DURATION = 800;
const INVESTOR_HEADER_HEIGHT = 70; // Should be synchronized with SCSS variable $header-height
const INVESTOR_SECONDARY_MENU_HEIGHT = 84; // Should be synchronized with SCSS variable $investor-secondary-menu-height
const SCROLL_TO_EXTRA_OFFSET = 30;
const HIGHLIGHT_ACTIVE_ANCHOR_DISTANCE_THRESHOLD = 250;

class InvestorSecondaryMenu extends React.PureComponent {
    static propTypes = {
        className: PropTypes.string,
        isHeaderHidden: PropTypes.bool,
        backToInvestorLink: PropTypes.shape(Link.propTypes),
        lang: PropTypes.shape({
            backToTopLabel: PropTypes.string.isRequired,
            secondaryMenuAriaLabel: PropTypes.string.isRequired,
        }).isRequired,
        links: PropTypes.arrayOf(PropTypes.shape(AnchorLink.propTypes)).isRequired,
        allowHeaderHidingOnScroll: PropTypes.func
    }

    state = {
        isScrolled: false,
        activeAnchorId: null,
        isCurrentlyNavigatingToAnchorSection: false,
    };

    menuRef = null;
    pageElement = null;
    staticPosition = null;

    componentDidMount() {
        this.originalScrollRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
    
        this.pageElement = document.querySelector('.site');
        if (this.menuRef) {
            this.staticPosition = this.menuRef.offsetTop;
        }
    
        this.syncActiveAnchorToScrollPosition();
    
        window.addEventListener('load', this.handlePageLoad);
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('hashchange', this.handleHashChange);
    }
    
    componentWillUnmount() {
        if (this.originalScrollRestoration) {
            window.history.scrollRestoration = this.originalScrollRestoration;
        }
    
        window.removeEventListener('load', this.handlePageLoad);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('hashchange', this.handleHashChange);
    
        if (this.pageElement) {
            this.pageElement.classList.remove(INVESTOR_PAGE_SECONDARY_MENU_STICKY_CLASSNAME);
        }
    }
    
    handlePageLoad = () => {
        const { links } = this.props;
        const hash = window.location.hash.slice(1);
    
        if (hash && links && links.length > 0) {
            setTimeout(() => {
                this.scrollToAnchor(hash);
            }, 100);
        }
    };

    componentDidUpdate(_, prevState) {
        const { isScrolled, activeAnchorId } = this.state;

        if (isScrolled !== prevState.isScrolled && this.pageElement) {
            this.pageElement.classList.toggle(INVESTOR_PAGE_SECONDARY_MENU_STICKY_CLASSNAME, isScrolled);
        }

        if (activeAnchorId !== prevState.activeAnchorId) {
            this.scrollActiveAnchorItemIntoView();
        }
    }

    handleScroll = () => {
        if (this.staticPosition !== null) {
            const isScrolled = window.scrollY >= this.staticPosition;

            if (isScrolled !== this.state.isScrolled) {
                this.setState({ isScrolled });
            }
        }

        this.syncActiveAnchorToScrollPosition();
    };

    setMenuRef = (element) => {
        this.menuRef = element;
    };

    setMenuListRef =  (element) => {
        this.menuListRef = element;
    };

    syncActiveAnchorToScrollPosition = () => {
        const { links } = this.props;

        if (this.state.isCurrentlyNavigatingToAnchorSection) {
            return;
        }
  
        let activeId = null;
    
        for (let index = 0; index < links.length; index++) {
            const anchorLink = links[index];
            const anchorElement = document.getElementById(anchorLink.anchorId);
            const rect = anchorElement ? anchorElement.getBoundingClientRect() : null;

            if (!rect) {
                break;
            }
    
            if (rect.top <= HIGHLIGHT_ACTIVE_ANCHOR_DISTANCE_THRESHOLD) {
                const nextTargetLink = links[index + 1];
                const nextTargetElement = nextTargetLink ? document.getElementById(nextTargetLink.anchorId) : null;
                if (
                    !nextTargetElement ||
                    (nextTargetElement &&
                        nextTargetElement.getBoundingClientRect().top >
                        HIGHLIGHT_ACTIVE_ANCHOR_DISTANCE_THRESHOLD)
                ) {
                    activeId = anchorLink.anchorId;
                    break;
                }
            }
        }

        this.setState({
            activeAnchorId: activeId
        });
    };

    scrollToAnchor = (id) => {
        const { links, isHeaderHidden } = this.props;
    
        if (this.anchorLinkClickTimeoutRef) {
            clearTimeout(this.anchorLinkClickTimeoutRef);
        }
    
        this.setState({ isCurrentlyNavigatingToAnchorSection: true });
    
        const targetAnchorLink = links.find(link => link.anchorId === id);
    
        if (targetAnchorLink) {
            const targetElement = document.getElementById(targetAnchorLink.anchorId);
    
            if (targetElement) {
                let scrollOffset = INVESTOR_SECONDARY_MENU_HEIGHT + SCROLL_TO_EXTRA_OFFSET;
    
                if (!isHeaderHidden) {
                    scrollOffset += INVESTOR_HEADER_HEIGHT;
                }
    
                this.props.allowHeaderHidingOnScroll(false);
    
                scrollToBlock(
                    targetElement,
                    scrollOffset
                );
            }
        }
    
        this.setState({ activeAnchorId: id });
    
        this.anchorLinkClickTimeoutRef = setTimeout(() => {
            this.setState({ isCurrentlyNavigatingToAnchorSection: false }, () => {
                if (!this.state.isCurrentlyNavigatingToAnchorSection) {
                    this.props.allowHeaderHidingOnScroll(true);
                }
            });
        }, ANCHOR_SCROLL_ANIMATION_DURATION);
    };

    handleHashChange = () => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            this.scrollToAnchor(hash);
        }
    };

    onAnchorLinkClick = (e, id) => {
        e.preventDefault();
        window.history.pushState(null, null, `#${id}`);
        window.dispatchEvent(new Event("hashchange"));
    };

    scrollToTop = (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        history.pushState("", document.title, window.location.pathname
            + window.location.search);
    }

    scrollActiveAnchorItemIntoView = () => {
        if (!this.menuListRef || !this.state.activeAnchorId) return;

        const listElement = this.menuListRef;
        const items = listElement.querySelectorAll('.investor-secondary-menu__link');

        const activeItem = Array.from(items).find(item => {
            const itemHref = item.getAttribute('href');
            if (!itemHref) return null;
            return itemHref.slice(1) === this.state.activeAnchorId;
        });

        if (listElement && activeItem) {
            const listRect = listElement.getBoundingClientRect();
            const activeRect = activeItem.getBoundingClientRect();

            const offsetLeft =
                activeRect.left - listRect.left + listElement.scrollLeft;
            const scrollTo = offsetLeft - listElement.clientWidth / 2 + activeRect.width / 2;

            listElement.scrollTo({
                left: scrollTo,
                behavior: "smooth",
            });
        }
    };

    render() {
        const { links, className, isHeaderHidden, backToInvestorLink, lang } = this.props;
        const { activeAnchorId, isScrolled } = this.state;

        if (!links || links.length === 0) return null;

        return (
            <nav
                ref={this.setMenuRef}
                className={cn('investor-secondary-menu', className, {
                    'investor-secondary-menu_scrolled': isHeaderHidden,
                })}
                aria-label={lang.secondaryMenuAriaLabel}
            >
                <div className="investor-secondary-menu__container">
                    {backToInvestorLink && (
                        <InvestorLinkButton
                            url={backToInvestorLink.url}
                            text={isScrolled ? lang.backToTopLabel : backToInvestorLink.linkText}
                            openInNewWindow={backToInvestorLink.openInNewWindow}
                            theme="ghost"
                            iconOnlyOnTablet={true}
                            className="investor-secondary-menu__nav-link"
                            iconName={isScrolled ? "arrow-line-top" : "arrow-line-left"}
                            onClick={isScrolled ? this.scrollToTop : null}
                        />
                    )}
                    <ul ref={this.setMenuListRef} className="investor-secondary-menu__list">
                        {links.map((link, index) => (
                            <li key={index} className="investor-secondary-menu__item">
                                <InvestorLinkButton
                                    url={`#${link.anchorId}`}
                                    text={link.text}
                                    className={cn('investor-secondary-menu__link', {
                                        'investor-secondary-menu__link_active': link.isActive,
                                    })}
                                    isActive={activeAnchorId === link.anchorId}
                                    onClick={e => this.onAnchorLinkClick(e, link.anchorId)}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </nav>
        );
    }
}

export default InvestorSecondaryMenu;



// WEBPACK FOOTER //
// ./src/components/InvestorSecondaryMenu/InvestorSecondaryMenu.jsx